/**
 * Times belong to the SALE, not to whoever is looking at them.
 *
 * 🔴 WHY THIS EXISTS. Jim, 2026-09-18: *"I think the times should always show
 * the auction's local time. I have this problem with our current software. When
 * I am traveling, I have to do calculations to make sure I am entering the right
 * auction times. It is annoying."*
 *
 * Every date on the platform used to be rendered with the VIEWER'S zone —
 * `toLocaleString(undefined, …)` — which is the default and is wrong for an
 * auction. A sale closes at 6pm at the warehouse whether the auctioneer is in
 * Chicago, in a hotel in Lisbon, or on a phone in California. Handing the
 * browser's zone to a form means the same typed number stores a different
 * instant depending on where the person was standing, and the only defence is
 * arithmetic done by a human at the keyboard.
 *
 * So a sale carries an IANA zone (`auctions.timezone`), and every time is
 * entered and displayed in it, with the zone NAMED so nobody has to guess.
 *
 * 🔴 NO DEPENDENCY. This is `Intl` and arithmetic — no date library. A timezone
 * database is exactly the thing not to own, and the runtime already ships one;
 * pulling in a library would add weight to every bundle for functions that fit
 * on a page. See `feedback_ownership_over_rental`: own the differentiated layer,
 * rent nothing that the platform already gives away.
 */

/** A `datetime-local` input value: `YYYY-MM-DDTHH:mm`, seconds optional. */
const LOCAL_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;

/**
 * The zone every fallback lands on.
 *
 * Matches `profiles.preferred_timezone`'s column default, which has been
 * `America/Chicago` since the first schema migration. Picking a different one
 * here would mean two answers to "what do we assume?" in one product.
 */
export const DEFAULT_TIME_ZONE = 'America/Chicago';

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function offsetFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (f === undefined) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

/**
 * Is this a zone the runtime actually knows?
 *
 * Guards every entry point, because an unknown zone makes `Intl` THROW rather
 * than degrade — and a sale row is the kind of place a typo ("America/Chicgo")
 * can sit for months before anyone opens it.
 */
export function isValidTimeZone(timeZone: string | null | undefined): boolean {
  if (typeof timeZone !== 'string' || timeZone.trim() === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** The zone to use, falling back through the house's and then the platform's. */
export function resolveTimeZone(...candidates: (string | null | undefined)[]): string {
  for (const c of candidates) {
    if (isValidTimeZone(c) && c !== null && c !== undefined) return c;
  }
  return DEFAULT_TIME_ZONE;
}

/**
 * How far ahead of UTC a zone was at a given instant, in milliseconds.
 *
 * Works by asking `Intl` what the wall clock read in that zone at that instant,
 * then reading those digits back as if they were UTC. The difference IS the
 * offset. This is the standard way to get a historical, DST-correct offset out
 * of the runtime's own timezone database without shipping a second copy of it.
 */
export function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = offsetFormatter(timeZone).formatToParts(new Date(instantMs));

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type);
    return part === undefined ? 0 : Number(part.value);
  };

  // `hourCycle: 'h23'` is specified above precisely so midnight reads 00 and not
  // 24; some engines have returned "24" under `hour12: false`, which would put
  // the offset a day out for exactly one hour a day.
  const hour = read('hour') % 24;

  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    hour,
    read('minute'),
    read('second'),
  );

  return asIfUtc - instantMs;
}

/**
 * A wall-clock time in a zone → the absolute instant, as a UTC ISO string.
 *
 * 🔴 TWO PASSES, NOT ONE, AND THE SECOND ONE IS THE POINT. The offset depends on
 * the instant, and the instant is what we are trying to find — so the first pass
 * guesses using the offset in force at the same digits read as UTC, and the
 * second pass re-asks at the answer. They differ only near a daylight-saving
 * boundary, which is exactly where a single-pass conversion is wrong by an hour
 * and nowhere else. An auction closing on the last Sunday of October is not a
 * hypothetical.
 *
 * Returns null for anything that is not a real wall-clock time, rather than
 * substituting one — same contract as the pickup scheduler, for the same reason.
 */
export function zonedLocalToIso(local: string, timeZone: string): string | null {
  const m = LOCAL_DATETIME.exec(local.trim());
  if (m === null) return null;
  if (!isValidTimeZone(timeZone)) return null;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  const second = m[6] === undefined ? 0 : Number(m[6]);

  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) {
    return null;
  }

  const asIfUtc = Date.UTC(year, month - 1, day, hour, minute, second);

  // A date the calendar does not have (31 February) rolls forward silently.
  const rolled = new Date(asIfUtc);
  if (
    rolled.getUTCFullYear() !== year ||
    rolled.getUTCMonth() !== month - 1 ||
    rolled.getUTCDate() !== day
  ) {
    return null;
  }

  const firstGuess = asIfUtc - zoneOffsetMs(asIfUtc, timeZone);
  const corrected = asIfUtc - zoneOffsetMs(firstGuess, timeZone);

  /*
   * On the hour a zone SKIPS each spring the typed time never happens, and on
   * the hour it repeats each autumn it happens twice. Neither is an error worth
   * refusing a save over — a collection window at 2:30am is not what anyone is
   * scheduling — so the arithmetic is allowed to settle on its answer, the same
   * way every date library resolves it. Documented rather than guarded because a
   * refusal here would be unactionable for the person reading it.
   */
  return new Date(corrected).toISOString();
}

/**
 * An absolute instant → the `datetime-local` value showing it in a zone.
 *
 * The exact inverse of `zonedLocalToIso`, so a form can load a stored time,
 * leave it alone and save it back unchanged. That round trip is not decorative:
 * without it every visit to an auction page would nudge its times by the
 * difference between two zones.
 */
export function isoToZonedLocal(iso: string | null | undefined, timeZone: string): string {
  if (iso === null || iso === undefined || iso.trim() === '') return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  if (!isValidTimeZone(timeZone)) return '';

  const parts = offsetFormatter(timeZone).formatToParts(new Date(ms));
  const read = (type: Intl.DateTimeFormatPartTypes): string => {
    const part = parts.find((p) => p.type === type);
    return part === undefined ? '' : part.value;
  };

  const hour = String(Number(read('hour')) % 24).padStart(2, '0');
  return `${read('year')}-${read('month')}-${read('day')}T${hour}:${read('minute')}`;
}

/**
 * The short name a reader recognises — "CDT", "PST", "GMT+1".
 *
 * 🔴 THE LABEL IS NOT DECORATION. Showing a sale's own time without naming the
 * zone is strictly worse than showing the viewer's, because it looks local and
 * is not. "6:00 PM" tells a bidder in California the wrong thing; "6:00 PM CDT"
 * tells them the truth and lets them do the sum if they want to.
 *
 * Abbreviations are taken from the runtime rather than a table of our own: they
 * are seasonal (CST/CDT), and a hardcoded list would go stale silently.
 */
export function zoneAbbreviation(iso: string | null | undefined, timeZone: string): string {
  if (iso === null || iso === undefined || iso.trim() === '') return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms) || !isValidTimeZone(timeZone)) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(new Date(ms));

  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

export interface ZonedFormatOptions extends Intl.DateTimeFormatOptions {
  /** Append the zone's short name. Defaults to true — see `zoneAbbreviation`. */
  withZone?: boolean;
  locale?: string;
}

/**
 * Format an instant in a sale's own zone, named.
 *
 * `locale` defaults to the reader's, because how a date is SPELLED is a reading
 * preference — a French bidder should see "20 sept." — while WHICH instant is
 * shown is a fact about the sale. Those are different questions and only the
 * second one belongs to the auction.
 */
export function formatInZone(
  iso: string | null | undefined,
  timeZone: string,
  options: ZonedFormatOptions = {},
): string {
  if (iso === null || iso === undefined || iso.trim() === '') return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';

  const zone = resolveTimeZone(timeZone);
  const { withZone = true, locale, ...intl } = options;

  const base = new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    ...(Object.keys(intl).length === 0
      ? { dateStyle: 'medium' as const, timeStyle: 'short' as const }
      : intl),
  }).format(new Date(ms));

  if (!withZone) return base;
  const abbr = zoneAbbreviation(iso, zone);
  return abbr === '' ? base : `${base} ${abbr}`;
}

/**
 * The zones a house picks from.
 *
 * 🔴 CURATED, NOT `Intl.supportedValuesOf('timeZone')`, AND THE REASON IS A
 * TRAP. That list is ~600 entries and includes `EST`, `CST`, `MST` — real IANA
 * names that are FIXED offsets and never observe daylight saving. A house that
 * scrolled to "EST" would run an hour out from Eastern time every summer, and
 * the symptom (every sale closes an hour late, half the year) is one nobody
 * would trace back to a dropdown. Region/city names carry the daylight rules;
 * the abbreviations do not.
 *
 * A stored zone outside this list is still honoured — `resolveTimeZone` only
 * asks whether the runtime knows it — so a house with an unusual one keeps it.
 * This list decides what is OFFERED, not what is allowed.
 *
 * US first because Phase 1 is local US auctions.
 */
export const COMMON_TIME_ZONES: readonly { id: string; label: string }[] = [
  { id: 'America/New_York', label: 'Eastern — New York' },
  { id: 'America/Chicago', label: 'Central — Chicago' },
  { id: 'America/Denver', label: 'Mountain — Denver' },
  { id: 'America/Phoenix', label: 'Arizona — Phoenix (no daylight saving)' },
  { id: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { id: 'America/Anchorage', label: 'Alaska — Anchorage' },
  { id: 'Pacific/Honolulu', label: 'Hawaii — Honolulu (no daylight saving)' },
  { id: 'America/Toronto', label: 'Canada — Toronto' },
  { id: 'America/Winnipeg', label: 'Canada — Winnipeg' },
  { id: 'America/Edmonton', label: 'Canada — Edmonton' },
  { id: 'America/Vancouver', label: 'Canada — Vancouver' },
  { id: 'America/Halifax', label: 'Canada — Halifax' },
  { id: 'America/Mexico_City', label: 'Mexico — Mexico City' },
  { id: 'Europe/London', label: 'UK — London' },
  { id: 'Europe/Dublin', label: 'Ireland — Dublin' },
  { id: 'Europe/Paris', label: 'France — Paris' },
  { id: 'Europe/Berlin', label: 'Germany — Berlin' },
  { id: 'Europe/Madrid', label: 'Spain — Madrid' },
  { id: 'Europe/Rome', label: 'Italy — Rome' },
  { id: 'Europe/Amsterdam', label: 'Netherlands — Amsterdam' },
  { id: 'Europe/Zurich', label: 'Switzerland — Zurich' },
  { id: 'Europe/Stockholm', label: 'Sweden — Stockholm' },
  { id: 'Europe/Warsaw', label: 'Poland — Warsaw' },
  { id: 'Europe/Lisbon', label: 'Portugal — Lisbon' },
  { id: 'Europe/Athens', label: 'Greece — Athens' },
  { id: 'Asia/Dubai', label: 'UAE — Dubai' },
  { id: 'Asia/Singapore', label: 'Singapore' },
  { id: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { id: 'Asia/Tokyo', label: 'Japan — Tokyo' },
  { id: 'Australia/Perth', label: 'Australia — Perth' },
  { id: 'Australia/Brisbane', label: 'Australia — Brisbane' },
  { id: 'Australia/Adelaide', label: 'Australia — Adelaide' },
  { id: 'Australia/Melbourne', label: 'Australia — Melbourne' },
  { id: 'Australia/Sydney', label: 'Australia — Sydney' },
  { id: 'Pacific/Auckland', label: 'New Zealand — Auckland' },
  { id: 'America/Sao_Paulo', label: 'Brazil — São Paulo' },
  { id: 'Africa/Johannesburg', label: 'South Africa — Johannesburg' },
  { id: 'UTC', label: 'UTC' },
];

/**
 * The browser's own zone, when it is one we can use.
 *
 * ⚠️ ONLY EVER A SEED FOR A HOUSE THAT HAS NOT SET ONE, never a fallback for a
 * sale. Jim's complaint is precisely that the machine's zone follows the person
 * and the sale does not, so anything that quietly re-introduces it downstream of
 * the house setting would bring the bug back.
 */
export function detectBrowserTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(tz) ? tz : null;
  } catch {
    return null;
  }
}
